import {useParams} from "react-router-dom";
import {Navigation} from "@/components/Navigation";
import {Categories} from "@/components/Categories";
import {Footer} from "@/components/Footer";
import {Card} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {
  Activity,
  BarChart,
  Clapperboard,
  Computer,
  Gamepad2,
  Info,
  Megaphone,
  Newspaper,
  TrendingUp,
  Wallet
} from "lucide-react";
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Poll, PollStatus} from "@/types/Poll.ts";
import {PollListItem} from "@/components/PollListItem.tsx";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {SEO} from "@/components/SEO.tsx";
import {Session} from "@supabase/supabase-js";
import {useAuth} from "@/contexts/AuthContext.tsx";

type TimeFrame = "daily" | "weekly" | "monthly";

interface LeaderBoard {
  userId: string;
  totalWinnings: number;
  totalPolls?: number;
  winRate?: number;
  username: string;
}

const CategoryPage = () => {
  const { category } = useParams();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("daily");
  const [filter, setFilter] = useState<"active" | "completed" | "finished">("active");
  /*const [userSession] = useState<Session | null>(() => {
    const cachedSession = localStorage.getItem('session');
    return cachedSession ? JSON.parse(cachedSession) : null;
  });*/
  const {user, session, loading} = useAuth();
  const {data: polls, isLoading, refetch} = useQuery({
    queryKey: ['polls', category],
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${session?.access_token}`
      }
      const response = await fetch(`${import.meta.env.VITE_API_URL}/polls?category=${category?.toLowerCase()}`,{
        method: 'GET',
        headers: session?.access_token ? headers : {},
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: Poll[] = await response.json();
      // console.log("polls:", data)
      return data
          // .filter(poll => parseInt(poll.status.toString()) === PollStatus.active);
    }
  });

  const {data: categoryLeaderboard, isLoading: isCategoryLeaderboardLoding} = useQuery({
    queryKey: ['categoryLeaderboard', category, timeFrame],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/leaderboard?category=${category?.toLowerCase()}&range=${timeFrame}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      return await response.json();
    }
  });

  /*useEffect(()=>{
    if(filter == 'active'){

    }
  },[filter])*/

  useEffect(() => {
    refetch();
  }, [category]);

  const filteredPolls = polls?.filter(poll =>
    filter === "active" ? poll.status == PollStatus.active : (filter === 'completed' ? poll.status == PollStatus.completed : poll.status == PollStatus.pending)
  );

  const getCategoryIcon = () => {
    switch (category?.toLowerCase()) {
      case 'sports':
        return Activity;
      case 'news':
        return Newspaper;
      case 'trending':
        return TrendingUp;
      case 'fun':
        return Clapperboard;
      case 'politics':
        return Megaphone;
      case 'technology':
        return Computer;
      case 'economy':
        return BarChart;
      case 'finance':
        return Wallet;
      case 'gaming':
        return Gamepad2;
      default:
        return Activity;
    }
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <div className="min-h-screen bg-background">
      <SEO
          title={category || "Categories"}
          description="Browse polls filtered by your selected category"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />
      <Categories />
      <main className="pt-24 bg-background">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <CategoryIcon className="w-6 h-6 text-primary dark:text-primary-dark" />
            <h2 className="text-3xl font-bold text-secondary dark:text-white capitalize">{category} Polls</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Explore and participate in {category?.toLowerCase()} related poll events</p>
        </div>

        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4 mb-8">
            <Button
              onClick={() => setFilter("active")}
              variant={filter === "active" ? "outline" : "outline"}
              className={`
                ${filter === "active" 
                  ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]" 
                  : "dark:text-white dark:hover:text-[#1F2937]"
                }
              `}
            >
              Active Polls
            </Button>
            <Button
                onClick={() => setFilter("finished")}
                variant={filter === "finished" ? "outline" : "outline"}
                className={`
                ${filter === "finished"
                    ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]"
                    : "dark:text-white dark:hover:text-[#1F2937]"
                }
              `}
            >
              Finished Polls
            </Button>
            <Button
              onClick={() => setFilter("completed")}
              variant={filter === "completed" ? "outline" : "outline"}
              className={`
                ${filter === "completed" 
                  ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]" 
                  : "dark:text-white dark:hover:text-[#1F2937]"
                }
              `}
            >
              Completed Polls
            </Button>


          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredPolls?.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {filter === "active"
                  ? "No active polls found in this category"
                  : filter === "completed"  ?  "No completed polls found in this category" : "No finished polls found in this category"
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
              {filteredPolls?.map((poll) => (
                <PollListItem refetch={refetch} poll={poll} key={poll.id} />
              ))}
            </div>
          )}

          <div>
            <h2 className="text-2xl font-bold text-secondary dark:text-white mb-6">Category Leaderboard</h2>
            <div className="flex gap-4 mb-6">
              <Button
                onClick={() => setTimeFrame("daily")}
                variant="outline"
                className={`
                  ${timeFrame === "daily" 
                    ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]" 
                    : "dark:text-white dark:hover:text-[#1F2937]"
                  }
                `}
              >
                Daily
              </Button>
              <Button
                onClick={() => setTimeFrame("weekly")}
                variant="outline"
                className={`
                  ${timeFrame === "weekly" 
                    ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]" 
                    : "dark:text-white dark:hover:text-[#1F2937]"
                  }
                `}
              >
                Weekly
              </Button>
              <Button
                onClick={() => setTimeFrame("monthly")}
                variant="outline"
                className={`
                  ${timeFrame === "monthly" 
                    ? "bg-[#E5F4EA] text-[#155231] hover:text-[#155231] dark:bg-[#1F2937] dark:text-white dark:hover:text-[#155231]" 
                    : "dark:text-white dark:hover:text-[#1F2937]"
                  }
                `}
              >
                Monthly
              </Button>
            </div>

            {isCategoryLeaderboardLoding ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : categoryLeaderboard?.length > 0 ? (
              <Card className="bg-background dark:bg-gray-800">
                <div className="p-6">
                  {categoryLeaderboard.map((entry: LeaderBoard, key: number) => (
                    <div
                      key={key}
                      className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0"
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-bold text-lg text-gray-600 dark:text-gray-300">#{key + 1}</span>
                        <span className="font-medium text-secondary dark:text-white">{entry.username}</span>
                      </div>
                      <span className="font-semibold text-primary dark:text-primary-dark">{entry.totalWinnings} points</span>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <Alert className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <Info className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <AlertDescription className="ml-2 text-gray-600 dark:text-gray-300">
                  No leaderboard data available for this {timeFrame} period. Be the first to participate in polls and earn points!
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;
